import ExecutionEnvironment from 'exenv';

import imageServerConfig from '../imageServerConfig';

import {
  ADD_ITEM_TO_CART,
  EVENT_SIZING_IMPRESSION,
  EXIT_SEARCH_REVIEWS,
  HIDE_SELECT_SIZE_TOOLTIP,
  HIGHLIGHT_SELECT_SIZE_TOOLTIP,
  PRODUCT_COLOR_CHANGED,
  PRODUCT_DESCRIPTION_TOGGLE,
  PRODUCT_SIZE_CHANGED,
  PRODUCT_SWATCH_CHANGE,
  RECEIVE_BRAND_PROMO,
  RECEIVE_GENERIC_SIZING_BIAS,
  RECEIVE_PDP_STORY_SYMPHONY_COMPONENTS,
  RECEIVE_PDP_SYMPHONY_COMPONENTS,
  RECEIVE_PRODUCT_DETAIL,
  RECEIVE_PRODUCT_REVIEWS,
  RECEIVE_SEARCH_REVIEWS,
  RECEIVE_SIMILAR_STYLES,
  RECEIVE_SIZING_PREDICTION_FAILURE,
  RECEIVE_SIZING_PREDICTION_SUCCESS,
  RECEIVE_UPVOTE_REVIEW_FAILURE,
  RECEIVE_UPVOTE_REVIEW_SUCCESS,
  REQUEST_PDP_STORY_SYMPHONY_COMPONENTS,
  REQUEST_PDP_SYMPHONY_COMPONENTS,
  REQUEST_PRODUCT_DETAIL,
  REQUEST_PRODUCT_REVIEWS,
  REQUEST_SEARCH_REVIEWS,
  REQUEST_SIMILAR_STYLES,
  REQUEST_UPVOTE_REVIEW,
  SET_CAROUSEL_INDEX,
  SET_DOC_META_PDP,
  SHOW_SELECT_SIZE_TOOLTIP,
  TOGGLE_OOS_BUTTON,
  TOGGLE_PDP_VISIBILITY,
  UNHIGHLIGHT_SELECT_SIZE_TOOLTIP,
  VALIDATE_DIMENSIONS
} from 'constants/reduxActions';
import { HYDRA_PDP_ODSP_LINK_CHANGE } from 'constants/hydraTests';
import { MOST_HELPFUL, NEWEST } from 'constants/appConstants';
import { fetchSearchSimilarity } from 'apis/calypso';
import { upvoteReview as upvoteReviewApi } from 'apis/cloudreviews';
import { isAssigned } from 'actions/ab';
import { err, redirectToAuthWithHistory, setError } from 'actions/errors';
import { fetchErrorMiddleware, fetchErrorMiddlewareMaybeJson } from 'middleware/fetchErrorMiddleware';
import { trackEvent } from 'helpers/analytics';
import { firePixelServer } from 'actions/pixelServer';
import { redirectTo, redirectToSearch } from 'actions/redirect';
import { absoluteImageUrl } from 'helpers/productImageHelpers';
import ProductUtils from 'helpers/ProductUtils';
import { isAllowedPreferredSubsite } from 'helpers/MarketplaceUtils';
import { buildSeoProductString, buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import { trackError } from 'helpers/ErrorUtils';
import { productBundle, productReviews } from 'apis/cloudcatalog';
import { genericSizeBias, sizingPrediction } from 'apis/opal';
import { getBrandPromo, getSymphonyPdpComponents, getSymphonySlots } from 'apis/mafia';
import { IS_EGC_NAME, IS_NUMBER_RE, PRODUCT_ASIN } from 'common/regex';
import marketplace from 'cfg/marketplace.json';
import { getMafiaAndCredentials } from 'store/ducks/readFromStore';

const { subsiteId: { desktop: subsiteId }, pdp: { includeTsdImagesParam = false } } = marketplace;

const REVIEW_SORTS = {
  [MOST_HELPFUL]: 'upVotes:desc,overallRating:desc,reviewDate:desc',
  [NEWEST]: 'reviewDate:desc'
};

const reviewSort = label => REVIEW_SORTS[label] || REVIEW_SORTS[MOST_HELPFUL];

export function togglePdpVisibility(visible) {
  return {
    type: TOGGLE_PDP_VISIBILITY,
    visible
  };
}

export function setCarouselIndex(index) {
  return {
    type: SET_CAROUSEL_INDEX,
    index
  };
}

function requestProductDetail() {
  return {
    type: REQUEST_PRODUCT_DETAIL,
    requestedAt: Date.now()
  };
}

export function productSwatchChange(colorId) {
  return {
    type: PRODUCT_SWATCH_CHANGE,
    colorId
  };
}

export function receiveProductDetail(productDetail, imageServerUrl, colorId, lookupKeyObject) {
  return {
    type: RECEIVE_PRODUCT_DETAIL,
    product: { detail: productDetail },
    imageServerUrl,
    colorId,
    lookupKeyObject,
    receivedAt: Date.now(),
    calledClientSide: typeof window !== 'undefined'
  };
}

export function productNotFound() {
  return setError(err.PRODUCT_DETAILS, null, 404);
}

function firePdpPixelServer(product, /* optional*/colorId) {
  const style = ProductUtils.getStyleByColor(product.styles, colorId);
  return firePixelServer('pdp', {
    product: {
      sku: product.productId,
      styleId: style.styleId,
      price: style.price.replace('$', ''),
      name: product.productName,
      brand: product.brandName,
      category: product.defaultProductType,
      subCategory: product.defaultCategory,
      gender: ProductUtils.getGender(product)
    }
  });
}

export function toggleOosButton(oosModalActive) {
  return {
    type: TOGGLE_OOS_BUTTON,
    oosModalActive
  };
}

function handleOos(dispatch, product) {
  const seoTerm = buildSeoProductString(product);
  if (seoTerm) {
    return dispatch(redirectToSearch(seoTerm));
  } else {
    return dispatch(productNotFound());
  }
}

export function requestUpvoteReview(reviewId) {
  return {
    type: REQUEST_UPVOTE_REVIEW,
    reviewId
  };
}

export function receiveUpvoteReviewSucess(reviewId) {
  return {
    type: RECEIVE_UPVOTE_REVIEW_SUCCESS,
    reviewId
  };
}

export function receiveUpvoteReviewFailure(reviewId) {
  return {
    type: RECEIVE_UPVOTE_REVIEW_FAILURE,
    reviewId
  };
}

export function upvoteReview(reviewId, returnTo) {
  return function(dispatch, getState) {
    dispatch(requestUpvoteReview(reviewId));

    const state = getState();
    const { environmentConfig: { api: { mafia: { url } } } } = state;
    const credentials = getMafiaAndCredentials(state);

    const upvoteFailure = info => {
      dispatch(receiveUpvoteReviewFailure(reviewId));
      trackError('NON-FATAL', 'Could not upvote a product review.', info);
    };
    const upvoteApiCall = upvoteReviewApi(url, credentials, reviewId);
    return (
      upvoteApiCall
        .then(response => {
          if (!response) {
            upvoteFailure();
          } else if (response.status === 200) {
            dispatch(receiveUpvoteReviewSucess(reviewId));
          } else if (response.status === 403) {
            redirectToAuthWithHistory(dispatch, returnTo);
          } else {
            upvoteFailure(`server responded with a status of ${response.status}`);
          }
        })
        .catch(upvoteFailure)
    );
  };
}

export function loadProductDetailPage() {
  const args = arguments;
  return function(dispatch, getState) {
    return dispatch(fetchProductDetail(...args))
      .then(() => {
        const { product: { detail }, error } = getState();
        // only do this if we have actually properly loaded a product, otherwise we'll get error noise in the logs
        if (detail && !error) {
          dispatch(setProductDocMeta(detail, args[1]?.colorId));
        }

      });
  };
}

export function getSymphonyContentAfterPdpLoad({ styles, productId }, colorId, getCall = getSymphonyComponents) {
  return dispatch => {
    const style = ProductUtils.getStyleByColor(styles, colorId);
    const { styleId } = style;
    return dispatch(getCall(productId, styleId));
  };
}

export function setProductDocMeta(product, colorId) {
  return { type: SET_DOC_META_PDP, metaPayload: { product, colorId } };
}

// Filter styles out if there are OOS and without a future hardLaunchDate
export function makefilteredNonHardLaunchDateOosStyles(detail) {
  const styles = detail.styles.filter(style => {
    const { hardLaunchDate } = style;
    const hasStock = ProductUtils.hasAvailableStock(style);
    const hasFutureHardLaunchDate = hardLaunchDate ? new Date(hardLaunchDate) > Date.now() : false;
    return hasStock || hasFutureHardLaunchDate;
  });
  return { ...detail, styles };
}

export function handleRedirectToPreferredSubsite({ product, colorId, isAllowedSubsite = isAllowedPreferredSubsite }) {
  return (dispatch, getState) => {

    const state = getState();

    const {
      url: { userAgent }, pageView: { pageType }
    } = state;

    const { preferredSubsite } = product;

    if (preferredSubsite) {
      const { url, id } = preferredSubsite;
      const domConditionalUserAgent = ExecutionEnvironment.canUseDOM ? navigator.userAgent : userAgent;
      const shouldRedirect = isAllowedSubsite(marketplace, id, domConditionalUserAgent);
      // we dont want to redirect for the reviews page https://github01.zappos.net/mweb/marty/issues/11165
      const isPdpPageType = pageType === 'product';
      if (shouldRedirect && isPdpPageType) {
        return dispatch(redirectTo(`https://${url}${buildSeoProductUrl(product, colorId)}`, 301));
      }
    }
  };
}

/**
 * Loads product data for the given product and stores it in the product reducer.
 * @param  {String|Object}  productId            Product to load either string productId, or an object with an asin, stockId, or productId field.
 * @param  {Object}  options              Optional flags-
 *                                        background(boolean=false) if true, does not set the isLoading flag while the request is running.  Additionally will not set full page error if request fails if it is "background" request.
 *                                          includeRecos(boolean=false) if calypso searchsimilarity should be sent.
 *                                          colorId(String)-- color of the product being viewed. only used for pixel server
 *                                          seoName(String) -- seo friendly name of product -- used to redirect to search if OOS
 *                                          firePixel(boolean=false) whether to fire the pdp pixel on load
 * @return {Promise}
*/
export function fetchProductDetail(lookupKeyObject, { background = false, includeRecos = false, includeTsdImages = includeTsdImagesParam, colorId, seoName, firePixel = false, errorOnOos = true, includeOos = false, callbackOnSuccess, filterNonHardLaunchDateOosStyles = false } = {}) {

  return function(dispatch, getState) {
    if (typeof lookupKeyObject === 'string' || typeof lookupKeyObject === 'number') {
      lookupKeyObject = { productId: '' + lookupKeyObject };
    }
    if (lookupKeyObject.productId && !IS_NUMBER_RE.test(lookupKeyObject.productId)) {
      dispatch(productNotFound());
      return Promise.reject(err.PRODUCT_DETAILS);
    } else if (lookupKeyObject.asin) {
      lookupKeyObject.asin = lookupKeyObject.asin.toUpperCase();
      if (!PRODUCT_ASIN.test(lookupKeyObject.asin)) {
        dispatch(productNotFound());
        return Promise.reject(err.PRODUCT_DETAILS);
      }
    } else if (lookupKeyObject.stockId && !IS_NUMBER_RE.test(lookupKeyObject.stockId)) {
      dispatch(productNotFound());
      return Promise.reject(err.PRODUCT_DETAILS);
    }

    const state = getState();
    const { environmentConfig: { api: { cloudcatalog: cloudcatalogInfo } } } = state;

    const imageServerOpts = imageServerConfig(state);
    if (!background) {
      dispatch(requestProductDetail());
    }

    const productRequest = productBundle(cloudcatalogInfo, { ...lookupKeyObject, includeTsdImages, includeOos })
      .then(fetchErrorMiddleware);

    const dispatchErrorMessageOrNotFound = function(error) {
      if (error && error.status === 404 && seoName) {
        dispatch(redirectToSearch(seoName));
      } else {
        if (!background) {
          dispatch(setError(err.PRODUCT_DETAILS, error));
        }
      }
      return Promise.reject(err.PRODUCT_DETAILS);
    };

    return productRequest.then(productResponse => {
      if (!productResponse.product || productResponse.product.length !== 1) {
        dispatch(productNotFound());
        // Also throw an error because this means the response returned without the data we need
        throw err.PRODUCT_DETAILS;
      }

      const product = filterNonHardLaunchDateOosStyles ? makefilteredNonHardLaunchDateOosStyles(productResponse.product[0]) : productResponse.product[0];

      if (IS_EGC_NAME.test(product.productName)) {
        return dispatch(redirectTo('/egiftcard'));
      }

      const { defaultImageUrl, styles } = product;
      const { imageServerUrl } = imageServerOpts;

      // If there are no styles, the product is effectively out of stock.
      if ((!styles || styles.length < 1) && errorOnOos) {
        return handleOos(dispatch, product);
      }

      if (lookupKeyObject.asin || lookupKeyObject.stockId) {
        const style = styles.find(style => style.stocks.find(stock => stock.asin === lookupKeyObject.asin || stock.stockId === lookupKeyObject.stockId));
        if (style) {
          ({ colorId } = style);
        }
      }

      product.hasHalfSizes = null;
      if (ProductUtils.isShoeType(product.defaultProductType) && product.sizing.allValues) {
        product.hasHalfSizes = product.sizing.allValues.some(({ value }) => value.includes('.5'));
      }

      dispatch(handleRedirectToPreferredSubsite({ product, colorId }));

      product.defaultImageUrl = absoluteImageUrl(defaultImageUrl, imageServerUrl);
      dispatch(receiveProductDetail(product, imageServerUrl, colorId, lookupKeyObject));
      if (callbackOnSuccess) {
        dispatch(callbackOnSuccess(product, colorId));
      }
      if (firePixel) {
        dispatch(firePdpPixelServer(product, colorId));
      }

      // If we're also fetching recos and/or reviews, then the resulting promise should resolve
      // when the recos are loaded, otherwise the promise is resolved when details are loaded
      if (includeRecos) {
        const { styleId } = styles[0];
        return fetchProductSearchSimilarity(product.productId, styleId)(dispatch, getState);
      }
      return product;
    }).catch(dispatchErrorMessageOrNotFound);
  };
}

function requestProductReviews(reviewsPage, reviewsOffset, orderBy) {
  return {
    type: REQUEST_PRODUCT_REVIEWS,
    requestedAt: Date.now(),
    reviewsPage,
    reviewsOffset,
    orderBy
  };
}

function receiveProductReviews(productId, reviewData) {
  return {
    type: RECEIVE_PRODUCT_REVIEWS,
    receivedAt: Date.now(),
    productId,
    reviewData
  };
}

export function requestSearchReviews(reviewsPage, searchTerm) {
  return {
    type: REQUEST_SEARCH_REVIEWS,
    reviewsPage,
    searchTerm
  };
}

export function receiveSearchReviews(searchResults) {
  return {
    type: RECEIVE_SEARCH_REVIEWS,
    searchResults
  };
}

export function exitSearchReviews() {
  return {
    type: EXIT_SEARCH_REVIEWS
  };
}

/**
* Sets the current search result review page and offset in the store, as well as loads
*/
export function fetchReviewSearchResults(productId, query, page = 1, offset = 0, isCrucial = true) {
  return function(dispatch, getState) {
    dispatch(requestSearchReviews(page, query));
    const state = getState();
    const { environmentConfig: { api: { cloudcatalog } } } = state;
    return productReviews(cloudcatalog, { productId, query, page, offset })
      .then(fetchErrorMiddleware)
      .then(reviewResponse => {
        dispatch(receiveSearchReviews(reviewResponse));
      })
      .catch(e => {
        if (isCrucial) {
          dispatch(setError(err.PRODUCT_DETAILS, e));
        } else {
          trackError('NON-FATAL', 'Could not load Product Review search results', e);
        }
      });
  };
}

/**
 * Sets the current review page and offset in the store, as well as loads
 * @param  {String}  productId        Product to load reviews for
 * @param  {Number}  [page=1]         what page of reviews to load
 * @param  {Number}  [offset=0]       how many reviews to load
 * @param  {Boolean} [isCrucial=true] whether a failed request should set the error state or simply log the error.  Defaults to showing the error page if an error is encountered.
 * @param {String} orderBy            sort reviews by (best aka most helpful or newest)
 * @return {Promise}                  fetch promise for loading reviews
 */
export function fetchProductReviews(productId, page = 1, offset = 0, isCrucial = true, orderBy = MOST_HELPFUL) {
  return function(dispatch, getState) {
    // only sort options are most helpful and newest, so if it is not one of them, sanitize it.
    if (orderBy !== MOST_HELPFUL && orderBy !== NEWEST) {
      orderBy = MOST_HELPFUL;
    }
    dispatch(requestProductReviews(page, offset, orderBy));
    const state = getState();
    const { environmentConfig: { api: { cloudcatalog: cloudcatalogInfo } } } = state;
    return productReviews(cloudcatalogInfo, { productId, page, offset, sort: reviewSort(orderBy) })
      .then(fetchErrorMiddleware)
      .then(reviewResponse => {
        dispatch(receiveProductReviews(productId, reviewResponse));
      })
      .catch(e => {
        if (isCrucial) {
          dispatch(setError(err.PRODUCT_DETAILS, e));
        } else {
          trackError('NON-FATAL', 'Could not load Product Reviews.', e);
        }
      });
  };
}

function requestSimilarStyles() {
  return {
    type: REQUEST_SIMILAR_STYLES,
    requestedAt: Date.now()
  };
}

export function receiveSimilarStyles(productId, similarStylesData) {
  return {
    type: RECEIVE_SIMILAR_STYLES,
    similarStylesData,
    productId,
    receivedAt: Date.now()
  };
}

// These are Calypso "searchSimilarity" recos not true janus recos.  Instead see actions/recos.
export function fetchProductSearchSimilarity(productId, styleId, type = 'moreLikeThis', limit = 10, page = 1, fetcher = fetchSearchSimilarity) {
  return function(dispatch, getState) {
    const state = getState();
    const { environmentConfig: { api: { calypso: { url: calypsoUrl } } } } = state;

    dispatch(requestSimilarStyles);
    return fetcher(calypsoUrl, { styleId, type, limit, page, subsiteId, opts: imageServerConfig(state) })
      .then(similarStyleResponse => {
        dispatch(receiveSimilarStyles(productId, similarStyleResponse));
      })
      .catch(e => {
        dispatch(receiveSimilarStyles(productId, { results:[] }));
        trackError('NON-FATAL', 'Could not load Similar Product Styles.', e);
      });
  };
}

export function productSizeChanged(dimensions) {
  return {
    type: PRODUCT_SIZE_CHANGED,
    dimensions
  };
}

export function toggleProductDescription() {
  return { type: PRODUCT_DESCRIPTION_TOGGLE };
}

export function validateDimensions(showValidation) {
  return { type: VALIDATE_DIMENSIONS, showValidation: !!showValidation };
}

export function showSelectSizeTooltip() {
  return {
    type: SHOW_SELECT_SIZE_TOOLTIP
  };
}

export function hideSelectSizeTooltip() {
  return {
    type: HIDE_SELECT_SIZE_TOOLTIP
  };
}

export function highlightSelectSizeTooltip() {
  return {
    type: HIGHLIGHT_SELECT_SIZE_TOOLTIP
  };
}

export function unhighlightSelectSizeTooltip() {
  return {
    type: UNHIGHLIGHT_SELECT_SIZE_TOOLTIP
  };
}

export function receiveSizingPredictionSuccess(sizingPredictionValue, colorId) {
  return {
    type: RECEIVE_SIZING_PREDICTION_SUCCESS,
    sizingPredictionValue,
    colorId
  };
}

export function receiveSizingPredictionFailure(isOnDemandEligible) {
  return {
    type: RECEIVE_SIZING_PREDICTION_FAILURE,
    isOnDemandEligible
  };
}

export function receiveGenericSizingBias(genericSizeBiases) {
  return {
    type: RECEIVE_GENERIC_SIZING_BIAS,
    genericSizeBiases
  };
}

export function fetchSizingPrediction(productId, colorId, sizingPredictionFetcher = sizingPrediction) {
  return function(dispatch, getState) {
    const state = getState();
    const { environmentConfig: { api: { opal: opalInfo } } } = state;
    return sizingPredictionFetcher(opalInfo, { productId })
      .then(fetchErrorMiddleware)
      .then(response => {
        const { prediction, error, onDemandEligible } = response;
        if (prediction) {
          dispatch(receiveSizingPredictionSuccess(prediction, colorId));
          trackEvent('TE_PDP_SIZING', `${productId}:SizeSelected:${prediction}`);
          trackEvent('TE_PDP_SIZING', `${productId}:SizeSuggested:${prediction}`);
        } else {
          dispatch(receiveSizingPredictionFailure(onDemandEligible));
          trackEvent('TE_PDP_SIZING', `${productId}:NoResponse:${error}`);
          if (isAssigned(HYDRA_PDP_ODSP_LINK_CHANGE, 1, state)) {
            // If there isn't an explicit sizing prediction available, get generic size bias data
            dispatch(fetchGenericSizingBias(productId));
          }
        }
      })
      .catch(e => {
        trackError('NON-FATAL', 'Could not load Sizing Prediction.', e);
      });
  };
}

export function fetchGenericSizingBias(productId) {
  return (dispatch, getState) => {
    const { environmentConfig: { api: { opal } } = {} } = getState();
    return genericSizeBias(opal, productId)
      .then(fetchErrorMiddleware)
      .then(({ sizeBiases }) => {
        if (!Array.isArray(sizeBiases) || !sizeBiases?.length) {
          return dispatch(receiveGenericSizingBias(null));
        }

        // Formating the size bias data here instead of every render
        // Some dont come back with scores so we must default to 0
        const sortedSizeBiases = sizeBiases.map(({ value, score = 0 }) => ({ value, score })).sort(({ score: sA }, { score: sB }) => sB - sA);
        const diffBetweenTopTwo = sortedSizeBiases[0].score - sortedSizeBiases[1].score;
        const topTwoAreNeighbors = Math.abs(
          parseFloat(sortedSizeBiases[0].value) - parseFloat(sortedSizeBiases[1].value)
        ) === 0.5;

        const sizeBiasObj = {};
        sortedSizeBiases.forEach(({ score, value }) => sizeBiasObj[value] = score);

        const genericSizeBiases = {
          sortedSizeBiases,
          topTwoAreNeighbors,
          sizeBiasObj,
          diffBetweenTopTwo,
          productId
        };

        dispatch(receiveGenericSizingBias(genericSizeBiases));
      })
      .catch(err => trackError('NON-FATAL', 'Could not load generic size bias.', err));
  };
}

export function receiveBrandPromo(response) {
  return {
    type: RECEIVE_BRAND_PROMO,
    brandPromo: response
  };
}

export function addItemToCart() {
  return {
    type: ADD_ITEM_TO_CART
  };
}

export function fetchBrandPromo(brandId, brandPromoFetcher = getBrandPromo) {
  return (dispatch, getState) => {
    const { environmentConfig: { api: { mafia: { url } } } } = getState();
    return brandPromoFetcher({ url }, brandId)
      .then(fetchErrorMiddlewareMaybeJson)
      .then(response => {
        dispatch(receiveBrandPromo(response));
      })
      .catch(e => { // Api doesn't return anything if no brand promo found. Need to reset brandPromo object
        dispatch(receiveBrandPromo('no-promo-data'));
        trackError('NON-FATAL', 'Could not load brand promo.', e);
      });
  };
}

export function selectedColorChanged(colorId) {
  return { type:  PRODUCT_COLOR_CHANGED, colorId };
}

export function fireSizingImpression(event, sizeObj) {
  return {
    type:  EVENT_SIZING_IMPRESSION,
    event,
    sizeObj
  };
}

export function requestPdpSymphonyComponents() {
  return {
    type: REQUEST_PDP_SYMPHONY_COMPONENTS
  };
}

export function receivePdpSymphonyComponents(response, productId, styleId) {
  return {
    type: RECEIVE_PDP_SYMPHONY_COMPONENTS,
    symphony: { ...response, productId, styleId }
  };
}

export function getSymphonyComponents(productId, styleId) {
  return (dispatch, getState) => {
    dispatch(requestPdpSymphonyComponents());
    const { environmentConfig: { api: { mafia } } } = getState();
    return getSymphonyPdpComponents(mafia, { productId, styleId })
      .then(fetchErrorMiddleware)
      .then(response => dispatch(receivePdpSymphonyComponents(response, productId, styleId)))
      .catch(err => {
        trackError('ERROR', `Failed to fetch PDP Symphony info for: productId: ${productId || 'NA'}, styleId: ${styleId || 'NA'}`, err);
      });
  };
}

export function requestPdpStorySymphonyComponents() {
  return {
    type: REQUEST_PDP_STORY_SYMPHONY_COMPONENTS
  };
}

export function receivePdpStorySymphonyComponents(response, productId) {
  return {
    type: RECEIVE_PDP_STORY_SYMPHONY_COMPONENTS,
    symphonyStory: { ...response, productId }
  };
}

export function getPdpStoriesSymphonyComponents(productId, symphonySlotFetcher = getSymphonySlots) {
  return (dispatch, getState) => {
    dispatch(requestPdpStorySymphonyComponents());
    const { environmentConfig: { api: { mafia } } } = getState();
    return symphonySlotFetcher(mafia, { pageName: productId, pageLayout: 'detail' })
      .then(fetchErrorMiddleware)
      .then(response => dispatch(receivePdpStorySymphonyComponents(response, productId)))
      .catch(err => {
        trackError('NON-FATAL', `Failed to fetch PDP Symphony story info for: productId: ${productId}`, err);
      });
  };
}
